import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Lock, Calendar, User, Tag, BookOpen, ArrowUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { articlesAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { convertGoogleDriveUrl } from '../lib/imageUtils';
import { prepareArticleContent } from '../lib/contentUtils';
import { CommentSection } from '../components/common/CommentSection';

const PLACEHOLDER_IMAGE = "https://images.unsplash.com/photo-1638738105885-3e43f3b156a3?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function ArticleDetail() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await articlesAPI.getOne(id);
        setArticle(response.data);
      } catch (err) {
        const errorMsg = err.response && err.response.status === 404 ? 'Article not found' : 'Failed to load article';
        setError(errorMsg);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [id]);

  // Handle smooth scrolling for citation links
  useEffect(() => {
    const handleCitationClick = (e) => {
      const target = e.target;
      if (target.classList.contains('citation-marker') || target.classList.contains('back-to-cite')) {
        e.preventDefault();
        const href = target.getAttribute('href');
        const targetElement = document.querySelector(href);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add highlight effect
          targetElement.classList.add('citation-highlight');
          setTimeout(() => {
            targetElement.classList.remove('citation-highlight');
          }, 2000);
        }
      }
    };

    document.addEventListener('click', handleCitationClick);
    return () => document.removeEventListener('click', handleCitationClick);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4" data-testid="article-error">
        <h1 className="font-heading text-2xl text-foreground mb-4">{error || 'Article not found'}</h1>
        <Link to="/articles">
          <Button variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Articles
          </Button>
        </Link>
      </div>
    );
  }

  const isRestricted = article.visibility === 'restricted' && !isAuthenticated;
  const hasContent = article.content && article.content.trim() !== '';
  const imageUrl = convertGoogleDriveUrl(article.featured_image) || PLACEHOLDER_IMAGE;
  const articleTags = article.tags || [];
  const articleCitations = article.citations || [];
  
  // Process and sanitize content
  const processedContent = prepareArticleContent(article.content, articleCitations);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="paper-texture min-h-screen" data-testid="article-detail-page">
      {/* Hero Image */}
      <div className="relative h-[40vh] min-h-[300px]">
        <img 
          src={imageUrl}
          alt={article.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="article-container">
            <Link to="/articles" className="inline-flex items-center text-primary-foreground/80 hover:text-primary-foreground mb-4 transition-colors">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Articles
            </Link>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="article-container py-12 md:py-16">
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="article-section"
        >
          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <span className="tag-badge">{article.category}</span>
            {article.visibility === 'restricted' && (
              <span className="flex items-center gap-1 text-sm text-secondary">
                <Lock className="h-4 w-4" />
                Free Access with Login
              </span>
            )}
          </div>

          <h1 className="article-title text-3xl md:text-4xl lg:text-5xl leading-tight mb-6">
            {article.title}
          </h1>

          {/* Author & Date */}
          <div className="flex flex-wrap items-center gap-6 text-muted-foreground">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span>Written by <strong>{article.author_name}</strong></span>
            </div>
            {article.editor_name && article.editor_id !== article.author_id && (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>Edited by <strong>{article.editor_name}</strong></span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>{formatDate(article.created_at)}</span>
            </div>
          </div>

          {/* Tags */}
          {articleTags.length > 0 && (
            <div className="flex items-center gap-2 mt-4 flex-wrap">
              <Tag className="h-4 w-4 text-muted-foreground" />
              {articleTags.map((tag, idx) => (
                <span key={idx} className="text-sm text-muted-foreground">
                  {tag}{idx < articleTags.length - 1 ? ',' : ''}
                </span>
              ))}
            </div>
          )}
        </motion.header>

        {/* Gold Divider */}
        <div className="gold-line mb-8"></div>

        {/* Excerpt */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="article-section"
        >
          <p className="text-xl text-foreground leading-relaxed font-accent italic">
            {article.excerpt}
          </p>
        </motion.div>

        {/* Full Content */}
        {isRestricted && !hasContent ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-muted/50 border border-border p-8 md:p-12 text-center rounded-lg"
            data-testid="login-required-message"
          >
            <Lock className="h-12 w-12 mx-auto text-accent mb-4" />
            <h3 className="font-heading text-2xl font-semibold text-foreground mb-2">
              Free Access to Full Article
            </h3>
            <p className="text-muted-foreground mb-4 max-w-md mx-auto">
              Create a free account to continue reading this heritage story in full, 
              including citations and original references.
            </p>
            <p className="text-sm text-muted-foreground/70 mb-6 max-w-sm mx-auto italic">
              Open, free access for readers interested in Jabalpur's heritage. 
              Registration helps us preserve and present original research responsibly.
            </p>
            <div className="flex items-center justify-center gap-4">
              <Link to="/login">
                <Button data-testid="article-login-btn">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button variant="outline" data-testid="article-register-btn">Create Free Account</Button>
              </Link>
            </div>
          </motion.div>
        ) : hasContent ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="article-section-lg"
          >
            {/* Main Content with drop cap */}
            <div 
              className="article-content drop-cap"
              dangerouslySetInnerHTML={{ __html: processedContent }}
            />

            {/* References Section */}
            {articleCitations.length > 0 && (
              <section className="references-section mt-12 pt-8 border-t border-border" id="references">
                <div className="flex items-center gap-2 mb-6">
                  <BookOpen className="h-5 w-5 text-accent" />
                  <h3 className="font-heading text-xl font-semibold text-foreground">
                    Sources & References
                  </h3>
                </div>
                <ol className="references-list space-y-3">
                  {articleCitations.map((citation, idx) => (
                    <li 
                      key={idx} 
                      id={`ref-${idx + 1}`}
                      className="reference-item flex items-start gap-3 p-3 rounded-md transition-colors"
                    >
                      <span className="reference-number shrink-0 w-6 h-6 bg-accent/10 text-accent rounded text-xs font-semibold flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <div className="reference-content flex-1 min-w-0">
                        <span 
                          className="citation-text"
                          dangerouslySetInnerHTML={{ __html: citation }} 
                        />
                      </div>
                      <a 
                        href={`#cite-${idx + 1}`}
                        className="back-to-cite shrink-0 text-accent hover:text-accent/80 transition-colors"
                        title="Back to text"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </a>
                    </li>
                  ))}
                </ol>
              </section>
            )}
          </motion.div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <p>Full content coming soon.</p>
          </div>
        )}
      </article>

      {/* Comments Section */}
      <div className="article-container py-8">
        <CommentSection 
          contentType="article" 
          contentId={id} 
          authorId={article?.author_id}
        />
      </div>

      {/* Related Articles Placeholder */}
      <section className="py-12 bg-muted/30 border-t border-border">
        <div className="article-container text-center">
          <Link to="/articles">
            <Button variant="outline">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Browse More Articles
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
